/* ==========================================================================
   PDP — Main product section interactivity  (build: atc-price-direct-1)
   --------------------------------------------------------------------------
   Everything cart/variant-critical (add-to-cart, variant switching, price
   sync, URL sync) is handled by Dawn's own product-info.js / product-form.js
   / <variant-selects> — this file only owns the parts that are custom to
   this section's markup: gallery thumbnail swapping, the one-time/subscribe
   purchase-option toggle, and the (placeholder) facts drawer open/close.
   Guarded throughout so a missing piece never throws.
   ========================================================================== */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  /* --- Gallery: thumbnail click + prev/next arrows swap the stage panel --
     Panels slide horizontally (see .pdp-mp__panel in the CSS). The slide
     DIRECTION is set here as data-slide-dir on the stage before the class
     swap: moving to a later panel = "next" (outgoing exits left, incoming
     enters from right), earlier = "prev" (mirror). The CSS reads that attr
     to park inactive panels on the correct side. Desktop only — on mobile
     the panels are a swipeable scroll-snap row and these controls are
     hidden, but the JS still works harmlessly if invoked. */
  function initGallery(root) {
    var gallery = root.querySelector('[data-pdp-gallery]');
    if (!gallery || gallery.__pdpBound) return;
    gallery.__pdpBound = true;

    var stage = gallery.querySelector('[data-pdp-stage]');
    var panels = Array.prototype.slice.call(gallery.querySelectorAll('[data-pdp-panel]'));
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-pdp-thumb]'));
    var prevBtn = gallery.querySelector('[data-pdp-prev]');
    var nextBtn = gallery.querySelector('[data-pdp-next]');
    if (!panels.length) return;

    function activeIndex() {
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].classList.contains('is-active')) return i;
      }
      return 0;
    }

    function updateArrows(index) {
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= panels.length - 1;
    }

    function activate(mediaId, dir) {
      var targetIndex = 0;
      panels.forEach(function (panel, i) {
        if (panel.getAttribute('data-media-id') === mediaId) targetIndex = i;
      });

      // Infer slide direction from current → target when not given.
      if (stage) {
        var from = activeIndex();
        var slideDir = dir || (targetIndex < from ? 'prev' : 'next');
        stage.setAttribute('data-slide-dir', slideDir);
      }

      panels.forEach(function (panel) {
        var match = panel.getAttribute('data-media-id') === mediaId;
        panel.classList.toggle('is-active', match);
        panel.hidden = !match;
      });
      thumbs.forEach(function (thumb) {
        var match = thumb.getAttribute('data-media-id') === mediaId;
        thumb.classList.toggle('is-active', match);
        thumb.setAttribute('aria-current', match ? 'true' : 'false');
      });
      updateArrows(targetIndex);
      if (window.pauseAllMedia) window.pauseAllMedia();
    }

    function step(delta) {
      var next = activeIndex() + delta;
      if (next < 0 || next >= panels.length) return;
      activate(panels[next].getAttribute('data-media-id'), delta > 0 ? 'next' : 'prev');
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        activate(thumb.getAttribute('data-media-id'));
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    function syncMobileProgress() {
      if (!stage || window.innerWidth > 989) return;
      var wrap = stage.closest('.pdp-mp__stage-wrap');
      if (!wrap) return;

      var maxScroll = stage.scrollWidth - stage.clientWidth;
      var thumbWidth = maxScroll > 0 ? Math.max((stage.clientWidth / stage.scrollWidth) * 100, 18) : 100;
      thumbWidth = Math.min(thumbWidth, 100);
      var maxStart = Math.max(100 - thumbWidth, 0);
      var start = maxScroll > 0 ? (stage.scrollLeft / maxScroll) * maxStart : 0;
      var end = Math.min(start + thumbWidth, 100);

      wrap.style.setProperty('--pdp-gallery-line-start', start.toFixed(2) + '%');
      wrap.style.setProperty('--pdp-gallery-line-end', end.toFixed(2) + '%');
    }

    if (stage) {
      stage.addEventListener('scroll', syncMobileProgress, { passive: true });
      window.addEventListener('resize', syncMobileProgress);
      syncMobileProgress();
    }

    updateArrows(activeIndex());
    gallery.pdpActivateMedia = activate;
  }

  /* --- Sticky gallery hard-lock: pin the sticky top to the gallery's ACTUAL
     on-load offset from the top of the viewport, so the sticky point equals
     where the gallery already sits — it locks from the first pixel of scroll
     with zero upward drift (the CSS `top` falls back to a static value; this
     makes it exact). Only on desktop (≥990px), where the gallery is sticky.
     Re-measured on resize; skipped while the header hasn't shrunk mid-scroll
     so the offset reflects the load layout. --- */
  function initGalleryStickyLock(root) {
    var gallery = root.querySelector('.pdp-mp__gallery--sticky');
    if (!gallery || gallery.__pdpStickyBound) return;
    gallery.__pdpStickyBound = true;

    function measure() {
      if (window.innerWidth < 990) {
        gallery.style.removeProperty('--pdp-sticky-top');
        return;
      }
      // Only meaningful before the gallery has started sticking. If the page is
      // at (or near) the top, the gallery's bounding top is its natural offset.
      if (window.pageYOffset <= 2) {
        var top = Math.round(gallery.getBoundingClientRect().top);
        if (top > 0) gallery.style.setProperty('--pdp-sticky-top', top + 'px');
      }
    }

    measure();
    // Re-measure once fonts/images settle and on resize; and any time we return
    // to the very top of the page (layout may have changed).
    window.addEventListener('load', measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', function () {
      if (window.pageYOffset <= 2) measure();
    }, { passive: true });
  }

  /* --- Sync gallery to the selected variant's featured media ------------- */
  function initVariantMediaSync(root) {
    if (!window.subscribe || !window.PUB_SUB_EVENTS) return;
    window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      var gallery = root.querySelector('[data-pdp-gallery]');
      if (variant && variant.featured_media && gallery && gallery.pdpActivateMedia) {
        gallery.pdpActivateMedia(String(variant.featured_media.id));
      }
    });
  }

  /* --- Mirror the live price into the Add-to-cart button ----------------
     The price shows inside the ATC button (Love Wellness style) and must
     reflect BOTH the selected variant/size AND the selected purchase option
     (Subscribe vs One-time):
       • Subscribe selected → the subscribe price ([data-pdp-subscribe-price],
         kept current by initPurchaseOptions when the frequency changes).
       • One-time selected  → the one-time/variant price
         ([data-pdp-onetime-price], = the live #price-{section} value which
         Dawn re-renders per variant).
     Compare-at (.pdp-mp__price-compare in the hidden #price row) shows the
     struck-through "was" price. Re-run on variantChange, purchase-option
     change, and frequency change. Guarded; a missing piece is a no-op. */
  function formatMoney(cents) {
    var value = parseInt(cents, 10);
    if (isNaN(value)) return '';

    var format = window.YGConfig && window.YGConfig.moneyFormat ? window.YGConfig.moneyFormat : 'Rs. {{amount}}';
    var money = value / 100;
    var noDecimals = format.indexOf('no_decimals') !== -1;
    var amount = money.toLocaleString('en-US', {
      minimumFractionDigits: noDecimals ? 0 : 2,
      maximumFractionDigits: noDecimals ? 0 : 2
    });

    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amount)
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/<[^>]*>/g, '');
  }

  function activeBundleQuantity(root) {
    return 1;
  }

  function syncLimitedBundlePrices(root) {
    var priceEls = root.querySelectorAll('[data-pdp-limited-bundle-price]');
    Array.prototype.forEach.call(priceEls, function (el) {
      var unitPrice = parseInt(el.getAttribute('data-unit-price'), 10);
      var qty = parseInt(el.getAttribute('data-bundle-quantity'), 10);
      // Price to show, in order of trust: what Shopify actually charges for
      // this quantity (measured from the cart, see probeBundleDiscount), then
      // a hand-entered offer price, then the plain total.
      var probed = parseInt(el.getAttribute('data-probed-total'), 10);
      var offerPrice = parseInt(el.getAttribute('data-offer-price'), 10);
      var total;
      if (!isNaN(probed) && probed > 0) total = probed;
      else if (!isNaN(offerPrice) && offerPrice > 0) total = offerPrice;
      else total = unitPrice * qty;
      if (!isNaN(total) && qty > 1) el.textContent = formatMoney(total);
    });

    var compareEls = root.querySelectorAll('[data-pdp-limited-bundle-compare]');
    Array.prototype.forEach.call(compareEls, function (el) {
      var unitPrice = parseInt(el.getAttribute('data-unit-price'), 10);
      var qty = parseInt(el.getAttribute('data-bundle-quantity'), 10);
      if (!isNaN(unitPrice) && qty > 1) el.textContent = formatMoney(unitPrice * qty);
    });
  }

  /* Ask Shopify what the bundle actually costs.

     An automatic discount is invisible to the product page — Liquid sees no
     discount data on a product or variant, and it only materialises once the
     items are in a cart. So rather than restating a price by hand (which drifts
     the moment the discount changes), we measure it: set this variant's line
     to the bundle quantity, read the discounted line total, then put the line
     back exactly as it was.

     This runs whatever is in the cart. An earlier version bailed out as soon
     as the cart held anything — the common case — so the card quietly fell
     back to the undiscounted total for most shoppers. Only this variant's
     line is touched, and only between the two writes. */
  function probeBundleDiscount(root, bundle) {
    var priceEl = bundle.querySelector('[data-pdp-limited-bundle-price]');
    if (!priceEl) return;

    // The price starts hidden so a first load doesn't flash the undiscounted
    // figure. Every exit from here has to reveal it, or the card ends up with
    // no price at all — hence showIt() on each bail-out below.
    // Marks BOTH: the price (which fades in) and the card (which drops its
    // skeleton). The whole card waits on this one signal so it appears in a
    // single step rather than the copy landing first and the price after.
    //
    // The artwork is waited on too. Revealing as soon as the price settled
    // uncovered a card whose image was still loading, so the pack shot's box
    // sat empty for a moment — the skeleton is meant to cover exactly that.
    // Images already cached resolve immediately; a broken one still resolves,
    // so a bad src cannot leave the card hidden.
    var revealed = false;
    var showIt = function () {
      if (revealed) return;
      revealed = true;
      bundle.classList.remove('is-probing');
      priceEl.classList.add('is-priced');

      var img = bundle.querySelector('.pdp-mp__limited-bundle-img');
      var done = function () { bundle.classList.add('is-priced'); };
      if (img && !img.complete) {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        // Never wait on a stalled image longer than the card is worth.
        window.setTimeout(done, 1200);
      } else {
        done();
      }
    };

    var variantInput = root.querySelector('.product-variant-id');
    var variantId = variantInput && variantInput.value;
    var qty = parseInt(bundle.getAttribute('data-bundle-quantity'), 10);
    if (!window.fetch || !variantId || isNaN(qty) || qty < 2) { showIt(); return; }

    // Remember the measurement for the session. Liquid can only render the
    // undiscounted total, so a fresh page always starts wrong and is corrected
    // a moment later — visible as a flash from full price to discounted. A
    // remembered figure is applied before paint on every later view, leaving
    // just the first load to do the round trip. Keyed by variant and quantity
    // so it can't leak across either.
    var cacheKey = 'ygBundle:' + variantId + ':' + qty;

    // Tell the stylesheet the probe owns the timing now, so its own reveal
    // timer stands down. Without this the CSS net fired partway through the
    // measurement and uncovered a card still showing the undiscounted price
    // with no compare-at and no saving badge, which then jumped a moment
    // later. Paired with the ceiling below, so a request that never settles
    // still can't leave the card shimmering forever.
    bundle.classList.add('is-probing');
    window.setTimeout(showIt, 6000);

    var reveal = showIt;
    var applyTotal = function (total, andSync) {
      if (!total || total <= 0) return;
      priceEl.setAttribute('data-probed-total', total);
      var compareEl = bundle.querySelector('[data-pdp-limited-bundle-compare]');
      var unit = parseInt(priceEl.getAttribute('data-unit-price'), 10);
      var full = unit * qty;
      if (compareEl && total < full) {
        compareEl.textContent = formatMoney(full);
        compareEl.hidden = false;
      }
      // Saving badge: only shown when the discount is real, and rounded down so
      // it can never overstate what the customer gets.
      var saveEl = bundle.querySelector('[data-pdp-limited-bundle-save]');
      if (saveEl && full > 0 && total < full) {
        var pct = Math.floor((full - total) / full * 100);
        if (pct >= 1) {
          saveEl.textContent = 'Save ' + pct + '%';
          saveEl.hidden = false;
        }
      }
      if (andSync) syncLimitedBundlePrices(root);
      reveal();
    };

    // A remembered total is only trusted when it still describes a discount:
    // if it equals the undiscounted figure it tells us nothing, and showing it
    // would present full price as though it were the offer. Anything at or
    // above full price is discarded and measured afresh.
    try {
      var cached = window.sessionStorage && sessionStorage.getItem(cacheKey);
      if (cached) {
        var cachedTotal = parseInt(cached, 10);
        var unitPrice = parseInt(priceEl.getAttribute('data-unit-price'), 10);
        var fullPrice = unitPrice * qty;
        if (cachedTotal > 0 && cachedTotal < fullPrice) {
          applyTotal(cachedTotal, true);
        } else {
          sessionStorage.removeItem(cacheKey);
        }
      }
    } catch (e) { /* storage unavailable — fall through to measuring */ }

    var json = function (url, opts) {
      return fetch(url, opts).then(function (r) { return r.json(); });
    };
    var post = function (url, body) {
      return json(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    };

    var lineFor = function (cart) {
      return ((cart && cart.items) || []).filter(function (i) {
        return String(i.variant_id) === String(variantId);
      })[0];
    };

    json('/cart.js')
      .then(function (cart) {
        if (!cart) return null;
        var existing = lineFor(cart);
        var had = existing ? existing.quantity : 0;
        if (had === qty) return existing.final_line_price;  // already measurable

        // Briefly set this variant's line to the bundle quantity, read the
        // discounted total, then put the line back exactly as it was — an
        // absent line is removed again, an existing one is restored to its
        // own quantity. Other lines are never touched.
        var setTo = function (quantity) {
          return existing || quantity === 0
            ? post('/cart/change.js', { id: String(variantId), quantity: quantity })
            : post('/cart/add.js', { items: [{ id: variantId, quantity: quantity }] });
        };

        return setTo(qty)
          .then(function () { return json('/cart.js'); })
          .then(function (after) {
            var line = lineFor(after);
            var total = line && line.final_line_price;
            return post('/cart/change.js', { id: String(variantId), quantity: had })
              .then(function () { return total; });
          });
      })
      .then(function (total) {
        if (total && total > 0) {
          applyTotal(total, true);
          try {
            if (window.sessionStorage) sessionStorage.setItem(cacheKey, String(total));
          } catch (e) { /* nothing to do if storage is full or blocked */ }
        }
        // Nothing measured (no discount on this quantity) — the Liquid-rendered
        // total is already correct, so just show it.
        reveal();
      })
      .catch(function () {
        // Display-only: keep whatever is rendered, but never leave it hidden.
        reveal();
      });
  }

  function syncBundleQuantity(root) {
    var qtyInput = root.querySelector('.quantity__input[name="quantity"]');
    if (!qtyInput) return;
    qtyInput.value = activeBundleQuantity(root);
  }

  function syncAtcPrice(root) {
    /* The button holds two identical labels -- the hover-slide reveals the
       second -- so there are two price blocks to keep in step. Writing only
       the first left the revealed copy showing the previous variant's price
       on hover. */
    root.querySelectorAll('[data-pdp-atc-price]').forEach(function (target) {
      syncOneAtcPrice(root, target);
    });
    /* Dawn writes the button's label onto the first copy only (the span
       marked data-submit-text), so mirror it onto the duplicate; otherwise
       the two read differently once a variant change has renamed one. */
    var labels = root.querySelectorAll('.pdp-mp__atc-label');
    if (labels.length > 1) {
      for (var i = 1; i < labels.length; i++) labels[i].textContent = labels[0].textContent;
    }
  }

  function syncOneAtcPrice(root, target) {
    if (!target) return;

    var priceRow = root.querySelector('.pdp-mp__price-row');
    var compare = priceRow ? priceRow.querySelector('.pdp-mp__price-compare') : null;
    var onetimeEl = root.querySelector('[data-pdp-onetime-price]');
    var subscribeEl = root.querySelector('[data-pdp-subscribe-price]');
    var checked = root.querySelector('[data-pdp-purchase-radio]:checked');
    var isSubscribe = checked ? checked.value === 'subscribe' : false;
    var quantity = activeBundleQuantity(root);
    var sourceCents = null;

    // Pick the price text for the active purchase option; fall back through
    // the available sources so it still works on products with no subscription.
    var nowText = '';
    if (isSubscribe && subscribeEl) {
      nowText = (subscribeEl.textContent || '').trim();
      sourceCents = parseInt(subscribeEl.getAttribute('data-unit-price'), 10);
    } else if (onetimeEl) {
      nowText = (onetimeEl.textContent || '').trim();
      sourceCents = parseInt(onetimeEl.getAttribute('data-unit-price'), 10);
    } else if (priceRow) {
      var p = priceRow.querySelector('.pdp-mp__price');
      nowText = p ? (p.textContent || '').trim() : '';
    }

    if (quantity > 1 && !isNaN(sourceCents)) nowText = formatMoney(sourceCents * quantity);

    var nowEl = target.querySelector('.pdp-mp__atc-price-now');
    if (nowEl && nowText) nowEl.textContent = nowText;

    // Compare-at only makes sense for the one-time price (it's the variant's
    // own compare_at); hide it for subscribe, which already shows a discount.
    var wasEl = target.querySelector('.pdp-mp__atc-price-was');
    var wasText = (!isSubscribe && quantity === 1 && compare) ? (compare.textContent || '').trim() : '';
    if (wasText) {
      if (!wasEl) {
        wasEl = document.createElement('s');
        wasEl.className = 'pdp-mp__atc-price-was';
        target.appendChild(wasEl);
      }
      wasEl.textContent = wasText;
      wasEl.hidden = false;
    } else if (wasEl) {
      wasEl.hidden = true;
    }
  }

  function initAtcPriceSync(root) {
    if (root.__pdpAtcPriceBound) return;
    root.__pdpAtcPriceBound = true;
    syncAtcPrice(root);

    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        if (variant && variant.price != null) {
          var onetimeEl = root.querySelector('[data-pdp-onetime-price]');
          if (onetimeEl) onetimeEl.setAttribute('data-unit-price', variant.price);
          root.querySelectorAll('[data-pdp-limited-bundle-price]').forEach(function (el) {
            el.setAttribute('data-unit-price', variant.price);
          });
          root.querySelectorAll('[data-pdp-limited-bundle-compare]').forEach(function (el) {
            el.setAttribute('data-unit-price', variant.price);
          });
          syncLimitedBundlePrices(root);
        }
        // Swap the bundle image to the newly selected variant's, so a product
        // whose variants differ in colour shows the one being bought rather
        // than the product's default. Skipped where the offer carries its own
        // pack shot from ygm.offer_image — that image is of the bundle, not of
        // a variant, so replacing it with a single bottle would be wrong.
        if (variant && variant.featured_image && variant.featured_image.src) {
          var src = variant.featured_image.src;
          root.querySelectorAll('.pdp-mp__limited-bundle-media:not([data-bundle-fixed-image]) .pdp-mp__limited-bundle-img').forEach(function (img) {
            img.src = src.replace(/(\.[a-z]+)(\?|$)/i, '_400x$1$2');
            img.removeAttribute('srcset');
          });
        }
        // Runs after Dawn has updated #price-{section} for the new variant.
        syncAtcPrice(root);
      });
    }

    // Purchase-option toggle + frequency change: both alter which price the
    // button should show. Delegated so it survives section re-renders.
    var purchase = root.querySelector('[data-pdp-purchase]');
    if (purchase) {
      purchase.addEventListener('change', function () { syncAtcPrice(root); });
    }
    // expose so initPurchaseOptions can trigger a refresh after it updates
    // the subscribe price on a frequency change.
    root.__pdpSyncAtcPrice = function () { syncAtcPrice(root); };
  }

  function initLimitedBundle(root) {
    var bundles = root.querySelectorAll('[data-pdp-limited-bundle]');
    if (!bundles.length || root.__pdpLimitedBundleBound) return;
    root.__pdpLimitedBundleBound = true;

    function showFormError(message) {
      var wrapper = root.querySelector('.product-form__error-message-wrapper');
      var target = wrapper && wrapper.querySelector('.product-form__error-message');
      if (target) target.textContent = message || window.cartStrings && window.cartStrings.error || 'There was an error. Please try again.';
      if (wrapper) wrapper.hidden = false;
    }

    function sync() {
      syncLimitedBundlePrices(root);
      if (root.__pdpSyncAtcPrice) root.__pdpSyncAtcPrice();
    }

    Array.prototype.forEach.call(bundles, function (bundle) {
      var claim = bundle.querySelector('[data-pdp-limited-bundle-claim]');

      if (claim) {
        claim.addEventListener('click', function () {
          var variantInput = root.querySelector('.product-variant-id');
          var variantId = variantInput && variantInput.value;
          if (!variantId || claim.disabled) return;

          var qty = parseInt(bundle.getAttribute('data-bundle-quantity'), 10);
          if (isNaN(qty) || qty < 2) qty = 2;

          // The bundle SETS the quantity rather than adding to it. /cart/add is
          // additive, so clicking Claim twice used to stack (3 + 3 = 6 in the
          // cart) — the customer asked for one 3-pack, not two.
          var formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', qty);

          // The bundle price is NOT sent from here. Shopify's cart API ignores
          // a submitted price and always charges the variant's real one (a
          // price the client could set would let anyone name their own), so the
          // saving has to come from a discount configured in Shopify — an
          // automatic one on the bundle quantity, which needs nothing here.

          var subscribeSelected = root.querySelector('[data-pdp-purchase-radio][value="subscribe"]:checked');
          var sellingPlan = root.querySelector('[name="selling_plan"]:not([disabled])');
          if (subscribeSelected && sellingPlan && sellingPlan.value) {
            formData.append('selling_plan', sellingPlan.value);
          }

          // Write into the label wrapper, not the button: the button holds the
          // two stacked copies that make the hover slide work, and setting
          // textContent on it would delete them for good. setLabel keeps both
          // copies in step so the label reads the same either way.
          var labelWrap = claim.querySelector('[data-pdp-limited-bundle-label]');
          var labelEls = labelWrap
            ? labelWrap.querySelectorAll('.pdp-mp__limited-bundle-cta-text')
            : [];
          var setLabel = function (text) {
            if (labelEls.length) {
              Array.prototype.forEach.call(labelEls, function (el) { el.textContent = text; });
            } else {
              claim.textContent = text;
            }
          };

          var oldText = labelEls.length ? labelEls[0].textContent : claim.textContent;
          claim.disabled = true;
          setLabel('Adding...');

          // Offer of a different product (custom.offer_product): add one of it
          // to the cart and go straight to checkout, like the bundle below.
          var offerVariant = bundle.getAttribute('data-offer-variant');
          if (offerVariant) {
            var offerData = new FormData();
            offerData.append('id', offerVariant);
            offerData.append('quantity', 1);
            var offerConfig = window.fetchConfig ? fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/json' } };
            if (offerConfig.headers) offerConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            if (offerConfig.headers) delete offerConfig.headers['Content-Type'];
            offerConfig.body = offerData;

            fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add', offerConfig)
              .then(function (r) { return r.json(); })
              .then(function (response) {
                if (response && response.status) {
                  showFormError(response.description || response.message);
                  claim.disabled = false;
                  setLabel(oldText);
                  return;
                }
                window.location.href = '/checkout';
              })
              .catch(function () {
                showFormError(window.cartStrings && window.cartStrings.error);
                claim.disabled = false;
                setLabel(oldText);
              });
            return;
          }

          var config = window.fetchConfig ? fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/json' } };
          if (config.headers) config.headers['X-Requested-With'] = 'XMLHttpRequest';
          if (config.headers) delete config.headers['Content-Type'];
          config.body = formData;

          // Drop any existing line for this variant first, so the bundle sets
          // the quantity instead of adding to whatever is already there.
          fetch((window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: String(variantId), quantity: 0 })
          })
            .catch(function () { /* nothing to clear is fine — carry on and add */ })
            .then(function () {
              return fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add', config);
            })
            .then(function (response) { return response.json(); })
            .then(function (response) {
              if (response && response.status) {
                showFormError(response.description || response.message);
                claim.disabled = false;
                setLabel(oldText);
                return;
              }
              // Straight to checkout. The saving comes from an automatic
              // discount configured in Shopify for the bundle quantity, which
              // applies itself at checkout — nothing to carry from here.
              window.location.href = '/checkout';
            })
            .catch(function () {
              showFormError(window.cartStrings && window.cartStrings.error);
              claim.disabled = false;
              setLabel(oldText);
            });
        });
      }

      // Measure the real discounted total once the page is idle, so the card
      // shows what Shopify will charge without anyone restating it by hand.
      probeBundleDiscount(root, bundle);
    });

    sync();
  }

  /* --- Purchase options: one-time vs subscribe --------------------------- */
  function initPurchaseOptions(root) {
    var widget = root.querySelector('[data-pdp-purchase]');
    if (!widget || widget.__pdpBound) return;
    widget.__pdpBound = true;

    var options = widget.querySelectorAll('[data-pdp-purchase-option]');
    var radios = widget.querySelectorAll('[data-pdp-purchase-radio]');
    var select = widget.querySelector('[data-pdp-selling-plan-select]');
    var hiddenInput = widget.querySelector('[data-pdp-selling-plan-input]');
    var subscribePriceEl = widget.querySelector('[data-pdp-subscribe-price]');
    var onetimePriceEl = widget.querySelector('[data-pdp-onetime-price]');
    var atcNowEl = root.querySelector('[data-pdp-atc-price] .pdp-mp__atc-price-now');
    var atcWasEl = root.querySelector('[data-pdp-atc-price] .pdp-mp__atc-price-was');
    var frequency = widget.querySelector('[data-pdp-frequency]');
    var frequencyTrigger = widget.querySelector('[data-pdp-frequency-trigger]');
    var frequencyLabel = widget.querySelector('[data-pdp-frequency-label]');
    var frequencyMenu = widget.querySelector('[data-pdp-frequency-menu]');
    var selectName = select ? select.getAttribute('name') : '';
    // Elements that show the SELECTED frequency's discount % (the "Save X%"
    // pill + the "X% off every order" benefit line). Updated on every plan
    // change from the option's data-discount, so the number always reflects
    // the real per-plan saving configured in Admin — never hardcoded.
    var savePill = widget.querySelector('[data-pdp-save-pill]');
    var savePct = widget.querySelector('[data-pdp-save-pct]');
    var saveBenefit = widget.querySelector('[data-pdp-save-benefit]');
    var savePctBenefit = widget.querySelector('[data-pdp-save-pct-benefit]');

    function applyDiscountFromOption(opt) {
      if (!opt) return;
      var d = parseInt(opt.getAttribute('data-discount'), 10);
      if (isNaN(d)) d = 0;
      // Pill: show "Save X%" when there's a discount, hide it otherwise.
      // Full "Save X%" text as one node so the space never collapses.
      if (savePill) savePill.hidden = d <= 0;
      if (savePct) savePct.textContent = 'Save ' + d + '%';
      // Benefit line: "X% off every order" when discounted, else "Save on every order".
      if (saveBenefit) {
        if (d > 0) {
          saveBenefit.innerHTML = '<span data-pdp-save-pct-benefit>' + d + '</span>% off every order';
        } else {
          saveBenefit.textContent = 'Save on every order';
        }
        savePctBenefit = widget.querySelector('[data-pdp-save-pct-benefit]');
      }
    }

    function sync() {
      var checked = widget.querySelector('[data-pdp-purchase-radio]:checked');
      var isSubscribe = checked ? checked.value === 'subscribe' : false;

      options.forEach(function (option) {
        var radio = option.querySelector('[data-pdp-purchase-radio]');
        option.classList.toggle('is-active', !!radio && radio.checked);
      });

      if (select) select.setAttribute('name', isSubscribe ? selectName : '');
      if (hiddenInput) hiddenInput.disabled = !isSubscribe;
      syncBundleQuantity(root);

      // Set the Add-to-cart button's price DIRECTLY here from the selected
      // option, so it always shows the price that will actually be added
      // (subscribe vs one-time) the instant the option changes — no dependency
      // on a separate listener/hover to refresh it.
      var srcEl = isSubscribe ? subscribePriceEl : onetimePriceEl;
      if (atcNowEl && srcEl) {
        var t = (srcEl.textContent || '').trim();
        if (t) atcNowEl.textContent = t;
      }
      // The struck-through "was" price only applies to the one-time option
      // (variant compare-at); hide it while Subscribe is selected.
      if (atcWasEl) atcWasEl.hidden = isSubscribe;
      // Also run the fuller sync (handles variant compare-at etc.) if present.
      if (root.__pdpSyncAtcPrice) root.__pdpSyncAtcPrice();
    }

    function closeFrequencyMenu() {
      if (!frequencyMenu || !frequencyTrigger) return;
      frequencyMenu.hidden = true;
      frequencyTrigger.setAttribute('aria-expanded', 'false');
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', sync);
    });

    function activateSubscribe() {
      var subscribeRadio = widget.querySelector('[data-pdp-purchase-radio][value="subscribe"]');
      if (subscribeRadio && !subscribeRadio.checked) {
        subscribeRadio.checked = true;
        subscribeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (select) {
      select.addEventListener('pointerdown', activateSubscribe);
      select.addEventListener('focus', activateSubscribe);
      select.addEventListener('change', function () {
        var opt = select.selectedOptions[0];
        var price = opt && opt.getAttribute('data-price');
        if (price && subscribePriceEl) subscribePriceEl.textContent = price;
        if (opt && subscribePriceEl && opt.getAttribute('data-price-cents')) {
          subscribePriceEl.setAttribute('data-unit-price', opt.getAttribute('data-price-cents'));
        }
        if (opt && frequencyLabel) frequencyLabel.textContent = (opt.textContent || '').trim();
        // Swap the "Save X%" pill + benefit % to this frequency's real discount.
        applyDiscountFromOption(opt);
        if (frequencyMenu) {
          Array.prototype.forEach.call(frequencyMenu.querySelectorAll('[role="option"]'), function (item) {
            var btn = item.querySelector('[data-pdp-frequency-option]');
            item.setAttribute('aria-selected', btn && btn.getAttribute('data-selling-plan') === select.value ? 'true' : 'false');
          });
        }
        // Refresh the Add-to-cart button price (it mirrors the subscribe
        // price while Subscribe is selected).
        if (root.__pdpSyncAtcPrice) root.__pdpSyncAtcPrice();
      });
    }

    if (frequencyTrigger && frequencyMenu && select) {
      frequencyTrigger.addEventListener('click', function () {
        activateSubscribe();
        var isOpen = frequencyTrigger.getAttribute('aria-expanded') === 'true';
        frequencyMenu.hidden = isOpen;
        frequencyTrigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });

      frequencyMenu.addEventListener('click', function (event) {
        var option = event.target.closest('[data-pdp-frequency-option]');
        if (!option) return;
        activateSubscribe();
        select.value = option.getAttribute('data-selling-plan');
        select.dispatchEvent(new Event('change', { bubbles: true }));
        closeFrequencyMenu();
      });

      document.addEventListener('click', function (event) {
        if (frequency && !frequency.contains(event.target)) closeFrequencyMenu();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeFrequencyMenu();
      });
    }

    sync();
  }

  /* Facts drawer open/close now lives in its own file,
     assets/pdp-facts-drawer.js, alongside its snippet
     (snippets/pdp-facts-drawer.liquid) — real build, not this
     section's concern anymore. */

  /* --- "Jump to FAQs" link: quick eased scroll, not CSS scroll-behavior --
     CSS scroll-behavior:smooth has no duration control (browsers pick
     their own), so a fixed-duration eased scroll needs a
     manual rAF loop driving window.scrollTo. Respects
     prefers-reduced-motion by skipping the animation and jumping
     straight there instead. */
  function initFaqScroll(root) {
    var link = root.querySelector('[data-pdp-faq-scroll]');
    if (!link || link.__pdpBound) return;
    link.__pdpBound = true;

    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    link.addEventListener('click', function (event) {
      var targetId = link.getAttribute('href');
      var target = targetId && document.querySelector(targetId);
      if (!target && targetId === '#faqs') {
        var faqSection = document.querySelector('.yg-faq');
        target = faqSection && (faqSection.closest('.shopify-section') || faqSection);
      }
      if (!target) return;

      event.preventDefault();

      var startY = window.pageYOffset;
      var headerOffset = 110;
      var endY = Math.max(target.getBoundingClientRect().top + startY - headerOffset, 0);

      var prefersReducedMotion = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        window.scrollTo(0, endY);
        return;
      }

      var duration = 900;
      var startTime = null;

      function step(timestamp) {
        if (startTime === null) startTime = timestamp;
        var progress = Math.min((timestamp - startTime) / duration, 1);
        window.scrollTo(0, startY + (endY - startY) * easeInOutCubic(progress));
        if (progress < 1) window.requestAnimationFrame(step);
      }

      window.requestAnimationFrame(step);
    });
  }

  /* --- Gallery video: clean click-to-play/pause (no native controls). Dawn's
     <deferred-media> injects the <video> from its <template> only after the
     poster is clicked, so we can't bind the element up front — instead we
     delegate a click on the stage: any click landing on a .pdp-mp__video
     toggles it. First interaction un-mutes for sound (the video is rendered
     muted+loop so its autoplay-on-inject is allowed). --- */
  function initGalleryVideoToggle(root) {
    var wraps = root.querySelectorAll('[data-pdp-gallery-video-wrap]');
    if (!wraps.length) return;

    function safePlay(video) {
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    Array.prototype.forEach.call(wraps, function (wrap) {
      if (wrap.__pdpVidBound) return;
      wrap.__pdpVidBound = true;

      var video = wrap.querySelector('[data-pdp-gallery-video]');
      var toggle = wrap.querySelector('[data-pdp-gallery-video-toggle]');
      if (!video) return;

      // Reflect real playback state onto the wrapper (drives the play/pause
      // icon CSS), so it stays correct even if the video loops or is paused
      // elsewhere.
      video.addEventListener('play', function () {
        wrap.classList.add('is-playing');
        if (toggle) toggle.setAttribute('aria-label', 'Pause video');
      });
      video.addEventListener('pause', function () {
        wrap.classList.remove('is-playing');
        if (toggle) toggle.setAttribute('aria-label', 'Play video');
      });

      function toggleState() {
        if (video.paused) {
          // First interaction is a user gesture → un-mute for sound.
          video.muted = false;
          if (video.volume === 0) video.volume = 1;
          safePlay(video);
        } else {
          video.pause();
        }
      }

      if (toggle) toggle.addEventListener('click', function (e) { e.preventDefault(); toggleState(); });

      // It autoplays muted on load; reflect that initial playing state.
      if (!video.paused) wrap.classList.add('is-playing');
    });
  }

  /* --- Featured Ingredients carousel: prev/next arrows scroll the
     scroll-snap card track by roughly one page (its own visible width), and
     disable themselves at each end. Pure scrolling — no layout state, so it
     coexists fine with the swipe/scroll-snap the CSS already gives. The
     track lives inside a <details> that's open by default; if it's ever
     collapsed the scroll math still holds once reopened (widths are read
     live on each click). --- */
  function initIngredientsCarousel(root) {
    var carousel = root.querySelector('[data-pdp-ing-carousel]');
    if (!carousel || carousel.__pdpBound) return;
    carousel.__pdpBound = true;

    var track = carousel.querySelector('[data-pdp-ing-track]');
    var prevBtn = carousel.parentNode && carousel.parentNode.querySelector('[data-pdp-ing-prev]');
    var nextBtn = carousel.parentNode && carousel.parentNode.querySelector('[data-pdp-ing-next]');
    if (!track) return;

    function updateArrows() {
      var maxScroll = track.scrollWidth - track.clientWidth - 1;
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= maxScroll;
    }

    function page(dir) {
      // ~One viewport of cards per click, matching the "two visible" layout.
      var amount = Math.max(track.clientWidth * 0.9, 200);
      track.scrollBy({ left: dir * amount, behavior: 'smooth' });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { page(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { page(1); });
    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    updateArrows();
  }

  /* --- Accordions: smooth open/close for the native <details> trio
     (Featured Ingredients / Product Benefits / How to use). Native details
     toggles instantly; this animates the content's height via the Web
     Animations API on each toggle, then hands control back to the browser so
     the [open] state stays real (semantics, find-in-page, no-JS fallback all
     keep working). Respects prefers-reduced-motion. --- */
  function initAccordions(root) {
    var items = Array.prototype.slice.call(
      (root || document).querySelectorAll('.pdp-mp__accordion')
    );
    if (!items.length) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Animate one <details> open or closed. Keeps content in the DOM for the
       whole transition (a closing <details> would otherwise display:none its
       content instantly). Cancels any in-flight animation on the same element
       so rapid clicks don't stack. */
    function animate(details, opening) {
      var content = details.querySelector('.pdp-mp__accordion-content');
      if (!content) return;

      if (details.__pdpAnim) details.__pdpAnim.cancel();

      /* Detach this <details> from its single-open `name` group for the
         duration of the animation. Without this, setting ANOTHER member's
         `open=true` makes the browser instantly `open=false` this one —
         display:none-ing its content mid-transition and cutting the collapse
         off (exactly the "cut" being fixed here). The name is restored once
         the animation settles. */
      if (details.__pdpName == null) {
        details.__pdpName = details.getAttribute('name') || '';
      }
      details.removeAttribute('name');

      content.style.overflow = 'hidden';
      details.open = true;

      var full = content.scrollHeight;
      var current = content.offsetHeight;
      var from = details.__pdpAnimating ? current : (opening ? 0 : full);
      var to = opening ? full : 0;

      details.__pdpAnimating = true;
      var anim = content.animate(
        [{ height: from + 'px' }, { height: to + 'px' }],
        { duration: 260, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
      details.__pdpAnim = anim;
      anim.onfinish = function () {
        details.__pdpAnimating = false;
        details.__pdpAnim = null;
        content.style.overflow = '';
        content.style.height = '';
        details.open = opening; // settle the real state (false hides content)
        // Restore group membership now that the animation is done.
        if (details.__pdpName) details.setAttribute('name', details.__pdpName);
        details.__pdpName = null;
      };
    }

    /* Members of the same single-open group = accordions sharing this one's
       `name` (that's exactly what makes the browser auto-close the others). */
    function groupSiblings(details) {
      var name = details.getAttribute('name');
      if (!name) return [];
      return items.filter(function (el) {
        return el !== details && el.getAttribute('name') === name;
      });
    }

    items.forEach(function (details) {
      if (details.__pdpAccBound) return;
      details.__pdpAccBound = true;

      var summary = details.querySelector('.pdp-mp__accordion-summary');
      var content = details.querySelector('.pdp-mp__accordion-content');
      if (!summary || !content) return;

      summary.addEventListener('click', function (event) {
        if (reduce) return; // let the browser toggle instantly
        event.preventDefault();

        // Open if currently closed; close if currently open.
        var opening = !details.open;

        if (opening) {
          // Smoothly collapse whatever else is open in this group FIRST, so the
          // outgoing panel eases shut instead of the browser cutting it off.
          groupSiblings(details).forEach(function (sib) {
            if (sib.open) animate(sib, false);
          });
        }
        animate(details, opening);
      });
    });
  }

  /* Mobile: place the price on the SAME line as the product title (title left,
     price right) — Love Wellness style. The price row lives further down the DOM
     (in the buy box), so CSS alone can't put it beside the title; this moves the
     node into the title block on mobile and restores it on desktop, tracking
     viewport width changes. */
  function initTitlePrice(root) {
    var title = (root || document).querySelector('.pdp-mp__title-h0, .pdp-mp__title-h1, .pdp-mp__title-h2');
    var price = (root || document).querySelector('.pdp-mp__price-row');
    if (!title || !price || title.__ygTitlePriceBound) return;
    title.__ygTitlePriceBound = true;
    // Remember the price's original home so we can put it back on desktop.
    var home = document.createComment('pdp-price-home');
    price.parentNode.insertBefore(home, price);
    function apply() {
      var mobile = window.innerWidth <= 989;
      if (mobile) {
        if (price.parentNode !== title) {
          title.classList.add('pdp-mp__title--with-price');
          title.appendChild(price);
        }
      } else if (home.parentNode) {
        title.classList.remove('pdp-mp__title--with-price');
        home.parentNode.insertBefore(price, home);
      }
    }
    apply();
    var t;
    window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(apply, 150); });
  }

  function initAll(scope) {
    var root = scope || document;
    try { initTitlePrice(root); } catch (e) {}
    try { initGallery(root); } catch (e) {}
    try { initGalleryStickyLock(root); } catch (e) {}
    try { initGalleryVideoToggle(root); } catch (e) {}
    try { initVariantMediaSync(root); } catch (e) {}
    try { initAtcPriceSync(root); } catch (e) {}
    try { initLimitedBundle(root); } catch (e) {}
    try { initFaqScroll(root); } catch (e) {}
    try { initPurchaseOptions(root); } catch (e) {}
    try { initIngredientsCarousel(root); } catch (e) {}
    try { initAccordions(root); } catch (e) {}
  }

  ready(function () { initAll(document); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
